import React, { useEffect, useMemo, useRef, useState } from "react";
import AppShell from "@/components/ascend/AppShell";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useTheme, contrastRatio } from "@/context/ThemeContext";
import { toast, Toaster } from "sonner";
import { Upload, Heart, HeartOff, Trash2, Volume2, Star, Save, RotateCcw, Sparkles, ChevronRight, Check, Palette, Layers, Type, Music, AlertTriangle, X, Plus, Camera, BookOpen } from "lucide-react";
import { playCompletionSound } from "@/lib/sound";
import { extractPaletteFromImage } from "@/lib/palette";
import QuoteLibraryPanel from "@/components/ascend/QuoteLibraryPanel";

export default function ThemeStudio() {
  const { theme, defaults, save, reload, applyThemeToDom } = useTheme();
  const [draft, setDraft] = useState(null);
  const [media, setMedia] = useState([]);
  const [presets, setPresets] = useState([]);
  const [customAffirmations, setCustomAffirmations] = useState([]);
  const [anime, setAnime] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [extractedPalette, setExtractedPalette] = useState([]);
  const [newPresetName, setNewPresetName] = useState("");
  const [newAff, setNewAff] = useState("");
  const debounceRef = useRef(null);
  const fileRef = useRef(null);
  const photoRef = useRef(null);

  useEffect(() => { if (theme && !draft) setDraft(structuredClone(theme)); }, [theme, draft]);

  useEffect(() => {
    (async () => {
      try {
        const [m, p, ca, an] = await Promise.all([
          api.get("/media?kind=background"),
          api.get("/theme/presets"),
          api.get("/custom-affirmations"),
          api.get("/me/anime"),
        ]);
        setMedia(m.data.items || []);
        setPresets(p.data.items || []);
        setCustomAffirmations(ca.data.items || []);
        setAnime((an.data.items || []).map((a) => ({ ...a, quotes: a.quotes || [] })));
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  // Debounced live-save; instant DOM apply.
  const change = (patch) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    applyThemeToDom(next);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        await save(next);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    }, 400);
  };
  const changePalette = (patch) => change({ palette: { ...draft.palette, ...patch, preset_id: "custom" } });

  const applyPreset = (preset) => {
    change({ palette: { preset_id: preset.id, ...preset.palette } });
  };

  const applyCustomPreset = async (p) => {
    try {
      const { data } = await api.post(`/theme/presets/${p.id}/apply`);
      setDraft(data);
      applyThemeToDom(data);
      reload();
      toast.success(`Applied "${p.name}".`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const savePreset = async () => {
    if (!newPresetName.trim() || !draft) return;
    try {
      const { data } = await api.post("/theme/presets", {
        name: newPresetName.trim(),
        theme_snapshot: draft,
      });
      setPresets((arr) => [data, ...arr]);
      setNewPresetName("");
      toast.success("Saved as preset.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deletePreset = async (id) => {
    try {
      await api.delete(`/theme/presets/${id}`);
      setPresets((arr) => arr.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetToDefault = (presetId) => {
    const preset = defaults?.presets?.find((p) => p.id === presetId);
    if (!preset) return;
    const next = {
      palette: { preset_id: preset.id, ...preset.palette },
      font_pairing_id: "journal",
      card_style: "rounded",
      card_surface: "solid",
      shadow_depth: "soft",
      background_mode: draft.background_mode || "none",
      active_background_ids: draft.active_background_ids || [],
      background_favorites: draft.background_favorites || [],
      background_opacity: draft.background_opacity ?? 30,
      background_blur: draft.background_blur ?? 6,
      sound_choice: draft.sound_choice || "silent",
    };
    setDraft(next);
    applyThemeToDom(next);
    clearTimeout(debounceRef.current);
    save(next).catch((e) => toast.error(formatApiError(e)));
    toast.success(`Reset to ${preset.name}.`);
  };

  // --- Media
  const onUploadFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image is over 5MB.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "background");
      // Let axios set `multipart/form-data; boundary=...` automatically —
      // overriding it strips the boundary and the server rejects the body.
      const { data } = await api.post("/media/upload", fd);
      setMedia((arr) => [data, ...arr]);
      toast.success("Uploaded.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };
  const removeMedia = async (id) => {
    try {
      await api.delete(`/media/${id}`);
      setMedia((arr) => arr.filter((m) => m.id !== id));
      // Also remove from active/favorites locally.
      change({
        active_background_ids: (draft.active_background_ids || []).filter((x) => x !== id),
        background_favorites: (draft.background_favorites || []).filter((x) => x !== id),
      });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const toggleActive = (id) => {
    const set = new Set(draft.active_background_ids || []);
    set.has(id) ? set.delete(id) : set.add(id);
    const active = Array.from(set);
    const mode = active.length === 0 ? "none" : draft.background_mode === "none" ? "single" : draft.background_mode;
    change({ active_background_ids: active, background_mode: mode });
  };
  const toggleFav = (id) => {
    const set = new Set(draft.background_favorites || []);
    set.has(id) ? set.delete(id) : set.add(id);
    change({ background_favorites: Array.from(set) });
  };

  // --- Custom affirmations
  const addAff = async () => {
    if (!newAff.trim()) return;
    try {
      const { data } = await api.post("/custom-affirmations", { text: newAff.trim() });
      setCustomAffirmations((arr) => [data, ...arr]);
      setNewAff("");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const deleteAff = async (id) => {
    try {
      await api.delete(`/custom-affirmations/${id}`);
      setCustomAffirmations((arr) => arr.filter((a) => a.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // --- Anime quotes editing
  const saveAnimeQuotes = async () => {
    try {
      const cleaned = anime.map((a) => ({
        title: (a.title || "").trim(),
        character: (a.character || "").trim() || null,
        quotes: (a.quotes || []).map((q) => q.trim()).filter(Boolean).slice(0, 3),
      })).filter((a) => a.title);
      await api.put("/me/anime", { items: cleaned });
      toast.success("Anime quotes saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!draft) return <AppShell><div className="text-muted">Loading studio…</div></AppShell>;

  const p = draft.palette;
  const contrast = contrastRatio(p.text_primary, p.background_base);

  return (
    <AppShell>
      <Toaster richColors position="top-center" />

      <div className="pt-2 mb-6 fade-up">
        <span className="eyebrow">make it yours</span>
        <h1 className="font-display text-4xl sm:text-5xl mt-1" style={{ color: "var(--text)" }}>Theme Studio</h1>
        <p className="text-muted mt-1">Every change previews live. Save moods as presets.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* PREVIEW */}
        <section className="lg:col-span-5 lg:sticky lg:top-6 self-start" data-testid="studio-preview">
          <PreviewCard palette={p} />
          {contrast < 4.5 && (
            <div className="mt-3 card-soft-2 p-3 text-sm flex items-start gap-2" data-testid="studio-contrast-warning" style={{ borderColor: "#E5A76E" }}>
              <AlertTriangle size={14} strokeWidth={1.75} className="mt-0.5 flex-shrink-0" style={{ color: "#B8862F" }} />
              <span style={{ color: "var(--text)" }}>
                Text may be hard to read on this background (contrast {contrast.toFixed(2)}:1). Consider a darker text or lighter background.
              </span>
            </div>
          )}
        </section>

        {/* CONTROLS */}
        <section className="lg:col-span-7 space-y-5">
          {/* PRESETS */}
          <StudioBlock icon={Star} label="starting palettes" testid="studio-block-presets">
            <div className="grid grid-cols-2 gap-2">
              {(defaults?.presets || []).map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => applyPreset(preset)}
                  className="preset-chip text-left"
                  data-testid={`studio-preset-${preset.id}`}
                  style={{
                    background: `linear-gradient(135deg, ${preset.palette.background_base}, ${preset.palette.background_gradient_stop})`,
                    color: preset.palette.text_primary,
                  }}
                >
                  <div className="font-display text-lg leading-tight">{preset.name}</div>
                  <div className="flex gap-1 mt-2">
                    {Object.values(preset.palette).slice(0, 5).map((c, i) => (
                      <span key={i} className="w-4 h-4 rounded-full border border-white/40" style={{ background: c }} />
                    ))}
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {(defaults?.presets || []).map((preset) => (
                <button
                  key={preset.id}
                  onClick={() => resetToDefault(preset.id)}
                  className="btn-ghost text-xs flex items-center gap-1"
                  data-testid={`studio-reset-${preset.id}`}
                >
                  <RotateCcw size={11} strokeWidth={1.5} /> reset to {preset.name}
                </button>
              ))}
            </div>
          </StudioBlock>

          {/* PALETTE */}
          <StudioBlock icon={Palette} label="palette" testid="studio-block-palette">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                ["primary", "Primary"],
                ["accent", "Accent"],
                ["background_base", "Background"],
                ["background_gradient_stop", "Gradient stop"],
                ["surface_card", "Card surface"],
                ["text_primary", "Text"],
                ["text_muted", "Text muted"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 card-soft-2 p-2" data-testid={`studio-color-${key}`}>
                  <input
                    type="color"
                    value={p[key]}
                    onChange={(e) => changePalette({ [key]: e.target.value })}
                    className="w-9 h-9 rounded-md cursor-pointer border-0"
                    aria-label={label}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-muted">{label}</div>
                    <input
                      type="text"
                      value={p[key]}
                      onChange={(e) => changePalette({ [key]: e.target.value })}
                      className="w-full bg-transparent text-xs font-mono outline-none"
                      style={{ color: "var(--text)" }}
                    />
                  </div>
                </label>
              ))}
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-muted">sample from a mood</div>
                <button
                  type="button"
                  onClick={() => photoRef.current?.click()}
                  className="btn-ghost text-xs flex items-center gap-1"
                  data-testid="studio-palette-from-photo-btn"
                  title="Client-side only — your photo never leaves your device."
                >
                  <Camera size={12} strokeWidth={1.5} /> palette from photo
                </button>
                <input
                  ref={photoRef}
                  type="file"
                  accept="image/*"
                  hidden
                  data-testid="studio-palette-from-photo-input"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    try {
                      const colors = await extractPaletteFromImage(f, 7);
                      setExtractedPalette(colors);
                      toast.success(`Extracted ${colors.length} colors — tap to sample.`);
                    } catch (err) {
                      toast.error("Couldn't read that image.");
                    } finally {
                      e.target.value = "";
                    }
                  }}
                />
              </div>
              {extractedPalette.length > 0 && (
                <div className="mb-2 flex items-center gap-2" data-testid="studio-palette-extracted">
                  <div className="text-xs w-32" style={{ color: "var(--text)" }}>from your photo</div>
                  <div className="flex gap-1 flex-1 flex-wrap">
                    {extractedPalette.map((c, i) => (
                      <button
                        key={i}
                        onClick={() => changePalette(
                          i === 0 ? { text_primary: c }
                          : i === 1 ? { primary: c, accent: c }
                          : i === 2 ? { accent: c }
                          : i === 3 ? { background_gradient_stop: c }
                          : { background_base: c, surface_card: c }
                        )}
                        className="w-6 h-6 rounded-md border border-white/40 transition-transform hover:scale-110"
                        style={{ background: c }}
                        aria-label={`Sample ${c}`}
                        data-testid={`studio-palette-extracted-${i}`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={() => setExtractedPalette([])}
                    className="p-1"
                    aria-label="Clear extracted palette"
                    data-testid="studio-palette-extracted-clear"
                  >
                    <X size={12} strokeWidth={1.5} />
                  </button>
                </div>
              )}
              <div className="space-y-2">
                {(defaults?.swatch_groups || []).map((g) => (
                  <div key={g.name} className="flex items-center gap-2" data-testid={`studio-swatch-group-${g.name}`}>
                    <div className="text-xs w-32 truncate" style={{ color: "var(--text)" }}>{g.name}</div>
                    <div className="flex gap-1 flex-1">
                      {g.colors.map((c, i) => (
                        <button
                          key={i}
                          onClick={() => changePalette(
                            i === 0 ? { text_primary: c }
                            : i === 1 ? { primary: c, accent: c }
                            : i === 2 ? { accent: c }
                            : i === 3 ? { background_gradient_stop: c }
                            : { background_base: c, surface_card: c }
                          )}
                          className="w-6 h-6 rounded-md border border-white/40 transition-transform hover:scale-110"
                          style={{ background: c }}
                          aria-label={`Sample ${c}`}
                          data-testid={`studio-swatch-${g.name}-${i}`}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </StudioBlock>

          {/* BACKGROUND */}
          <StudioBlock icon={Layers} label="background art" testid="studio-block-bg">
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => onUploadFile(e.target.files?.[0])} data-testid="studio-bg-file-input" />
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <button className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="studio-bg-upload-btn">
                <Upload size={14} strokeWidth={1.5} /> {uploading ? "Uploading…" : `Upload (${media.length}/12)`}
              </button>
              <div className="text-xs text-muted">JPG / PNG / WEBP · up to 5MB · 12 max</div>
            </div>

            {/* Curated */}
            <div className="mb-3">
              <div className="text-xs uppercase tracking-wider text-muted mb-2">curated</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {(defaults?.backgrounds || []).map((b) => {
                  const active = (draft.active_background_ids || []).includes(b.id);
                  return (
                    <button
                      key={b.id}
                      onClick={() => toggleActive(b.id)}
                      className="rounded-xl aspect-video overflow-hidden relative border transition-transform hover:scale-[1.02]"
                      style={{ background: b.css, borderColor: active ? "var(--primary)" : "var(--border)", borderWidth: active ? 2 : 1 }}
                      data-testid={`studio-bg-curated-${b.id}`}
                      title={b.name}
                    >
                      {active && (
                        <span className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "var(--primary)", color: "#fff" }}>
                          <Check size={12} strokeWidth={2} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Uploads */}
            {media.length > 0 && (
              <div className="mb-3">
                <div className="text-xs uppercase tracking-wider text-muted mb-2">your uploads</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {media.map((m) => {
                    const active = (draft.active_background_ids || []).includes(m.id);
                    const fav = (draft.background_favorites || []).includes(m.id);
                    return (
                      <div
                        key={m.id}
                        className="rounded-xl aspect-video overflow-hidden relative border cursor-pointer"
                        style={{ borderColor: active ? "var(--primary)" : "var(--border)", borderWidth: active ? 2 : 1, background: `url("${API_BASE}/media/${m.id}") center/cover` }}
                        onClick={() => toggleActive(m.id)}
                        data-testid={`studio-bg-user-${m.id}`}
                      >
                        {active && (
                          <span className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "var(--primary)", color: "#fff" }}>
                            <Check size={12} strokeWidth={2} />
                          </span>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleFav(m.id); }}
                          className="absolute top-1 left-1 w-5 h-5 rounded-full flex items-center justify-center bg-white/70 backdrop-blur-sm"
                          aria-label={fav ? "Unfavourite" : "Favourite"}
                          data-testid={`studio-bg-fav-${m.id}`}
                        >
                          <Heart size={11} strokeWidth={1.5} fill={fav ? "var(--primary)" : "none"} color="var(--primary)" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeMedia(m.id); }}
                          className="absolute bottom-1 right-1 w-5 h-5 rounded-full flex items-center justify-center bg-white/70 backdrop-blur-sm"
                          aria-label="Delete"
                          data-testid={`studio-bg-delete-${m.id}`}
                        >
                          <X size={11} strokeWidth={1.75} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-2 mb-3">
              {["none", "single", "rotate"].map((m) => (
                <button
                  key={m}
                  onClick={() => change({ background_mode: m })}
                  className="rounded-full py-2 text-xs font-medium"
                  style={{
                    background: draft.background_mode === m ? "var(--primary)" : "var(--surface-2)",
                    color: draft.background_mode === m ? "#fff" : "var(--text)",
                    border: "1px solid var(--border)",
                  }}
                  data-testid={`studio-bg-mode-${m}`}
                >
                  {m === "none" ? "off" : m === "single" ? "single" : "rotate daily"}
                </button>
              ))}
            </div>

            <Slider label="opacity" min={0} max={100} value={draft.background_opacity ?? 30} onChange={(v) => change({ background_opacity: v })} testid="studio-bg-opacity" suffix="%" />
            <Slider label="blur" min={0} max={20} value={draft.background_blur ?? 6} onChange={(v) => change({ background_blur: v })} testid="studio-bg-blur" suffix="px" />
          </StudioBlock>

          {/* TYPOGRAPHY */}
          <StudioBlock icon={Type} label="typography" testid="studio-block-fonts">
            <div className="grid grid-cols-2 gap-2">
              {(defaults?.font_pairings || []).map((f) => (
                <button
                  key={f.id}
                  onClick={() => change({ font_pairing_id: f.id })}
                  className="rounded-2xl p-3 text-left transition-all"
                  style={{
                    background: draft.font_pairing_id === f.id ? "color-mix(in oklab, var(--primary) 20%, var(--surface))" : "var(--surface-2)",
                    border: `1px solid ${draft.font_pairing_id === f.id ? "var(--primary)" : "var(--border)"}`,
                  }}
                  data-testid={`studio-font-${f.id}`}
                >
                  <div className="text-xs uppercase tracking-wider text-muted">{f.name}</div>
                  <div className="text-lg leading-tight" style={{ fontFamily: `'${f.heading}', serif`, color: "var(--text)" }}>Psychology</div>
                  <div className="text-xs" style={{ fontFamily: `'${f.body}', sans-serif`, color: "var(--text-muted)" }}>with a warm body voice</div>
                </button>
              ))}
            </div>
          </StudioBlock>

          {/* CARD STYLE */}
          <StudioBlock icon={Sparkles} label="card style" testid="studio-block-cards">
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Segmented value={draft.card_style} options={[["rounded", "Rounded"], ["sharp", "Sharp"]]} onChange={(v) => change({ card_style: v })} testid="studio-card-style" />
              <Segmented value={draft.card_surface} options={[["solid", "Solid"], ["glass", "Glass"]]} onChange={(v) => change({ card_surface: v })} testid="studio-card-surface" />
            </div>
            <Segmented value={draft.shadow_depth} options={[["none", "No shadow"], ["soft", "Soft"], ["warm", "Warm"]]} onChange={(v) => change({ shadow_depth: v })} testid="studio-shadow" />
          </StudioBlock>

          {/* MUSIC */}
          <StudioBlock icon={Music} label="focus music (during study block)" testid="studio-block-music">
            <label className="flex items-center gap-2 mb-3">
              <input type="checkbox" className="accent-[var(--primary)]" checked={!!draft.music_enabled_during_focus} onChange={(e) => change({ music_enabled_during_focus: e.target.checked })} data-testid="studio-music-enabled" />
              <span className="text-sm" style={{ color: "var(--text)" }}>Play classical music during focus sessions</span>
            </label>
            <MusicTrackPicker
              value={draft.music_default_track_id}
              onChange={(id) => change({ music_default_track_id: id })}
            />
            <Slider label="default volume" min={0} max={100} value={draft.music_default_volume ?? 30} onChange={(v) => change({ music_default_volume: v })} testid="studio-music-volume" suffix="%" />
          </StudioBlock>

          {/* SOUND */}
          <StudioBlock icon={Music} label="focus completion sound" testid="studio-block-sound">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(defaults?.sounds || []).map((s) => (
                <button
                  key={s.id}
                  onClick={() => { change({ sound_choice: s.id }); if (s.id !== "silent") playCompletionSound(s.id); }}
                  className="rounded-2xl px-3 py-3 text-sm flex items-center justify-center gap-2"
                  style={{
                    background: draft.sound_choice === s.id ? "color-mix(in oklab, var(--primary) 20%, var(--surface))" : "var(--surface-2)",
                    border: `1px solid ${draft.sound_choice === s.id ? "var(--primary)" : "var(--border)"}`,
                    color: "var(--text)",
                  }}
                  data-testid={`studio-sound-${s.id}`}
                >
                  <Volume2 size={12} strokeWidth={1.5} />
                  {s.name}
                </button>
              ))}
            </div>
            <div className="text-xs text-muted mt-2">plays gently when a focus block completes</div>
          </StudioBlock>

          {/* PRESETS SAVE */}
          <StudioBlock icon={Save} label="save this look as a preset" testid="studio-block-save-preset">
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <input
                className="input-soft flex-1"
                placeholder="Preset name (e.g. Exam Mode, Cozy Winter)"
                value={newPresetName}
                onChange={(e) => setNewPresetName(e.target.value)}
                maxLength={60}
                data-testid="studio-preset-name-input"
              />
              <button
                onClick={savePreset}
                disabled={!newPresetName.trim() || presets.length >= 5}
                className="btn-primary flex items-center gap-2 disabled:opacity-60"
                data-testid="studio-preset-save-btn"
              >
                <Save size={14} strokeWidth={1.5} /> Save preset ({presets.length}/5)
              </button>
            </div>
            {presets.length === 0 ? (
              <div className="text-center py-6 font-hand text-lg text-muted" data-testid="studio-preset-list-empty">
                no presets yet — save this look and switch moods later
              </div>
            ) : (
              <ul className="space-y-2" data-testid="studio-preset-list">
                {presets.map((p) => (
                  <li key={p.id} className="card-soft-2 p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg leading-tight" style={{ color: "var(--text)" }}>{p.name}</div>
                      <div className="flex gap-1 mt-1">
                        {Object.values(p.theme_snapshot?.palette || {}).slice(1, 6).map((c, i) => (
                          <span key={i} className="w-3 h-3 rounded-full" style={{ background: c }} />
                        ))}
                      </div>
                    </div>
                    <button className="btn-ghost text-xs" onClick={() => applyCustomPreset(p)} data-testid={`studio-preset-apply-${p.id}`}>apply</button>
                    <button className="p-2" onClick={() => deletePreset(p.id)} data-testid={`studio-preset-delete-${p.id}`} aria-label="Delete">
                      <Trash2 size={12} strokeWidth={1.5} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </StudioBlock>
          <StudioBlock icon={Sparkles} label="your voice — custom affirmations" testid="studio-block-voice">
            <p className="text-sm text-muted mb-3">Add your own lines. They'll join your anime quotes and dream lines in the rotation.</p>
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <input
                className="input-soft flex-1"
                placeholder="Type something you want to hear on a hard day…"
                value={newAff}
                onChange={(e) => setNewAff(e.target.value)}
                maxLength={280}
                data-testid="studio-aff-input"
              />
              <button onClick={addAff} disabled={!newAff.trim()} className="btn-primary flex items-center gap-2 disabled:opacity-60" data-testid="studio-aff-add-btn">
                <Plus size={14} strokeWidth={1.5} /> Add
              </button>
            </div>
            <ul className="space-y-2" data-testid="studio-aff-list">
              {customAffirmations.length === 0 && (
                <li className="text-center py-6 font-hand text-lg text-muted">nothing yet — say something kind to future-you</li>
              )}
              {customAffirmations.map((a) => (
                <li key={a.id} className="card-soft-2 p-3 flex items-center gap-3">
                  <blockquote className="flex-1 italic font-display text-base" style={{ color: "var(--text)" }}>"{a.text}"</blockquote>
                  <button onClick={() => deleteAff(a.id)} className="p-2" data-testid={`studio-aff-delete-${a.id}`} aria-label="Delete">
                    <Trash2 size={12} strokeWidth={1.5} />
                  </button>
                </li>
              ))}
            </ul>
          </StudioBlock>

          {/* ANIME QUOTES editing */}
          <StudioBlock icon={Sparkles} label="anime quotes (edit anytime)" testid="studio-block-anime">
            <div className="space-y-3" data-testid="studio-anime-list">
              {anime.length === 0 && <div className="font-hand text-lg text-muted text-center py-4">nothing here — add anime in Settings later.</div>}
              {anime.map((a, idx) => (
                <div key={idx} className="card-soft-2 p-3" data-testid={`studio-anime-${idx}`}>
                  <div className="font-display text-lg" style={{ color: "var(--text)" }}>{a.title}</div>
                  <div className="text-xs text-muted mb-2">{a.character || "no character set"}</div>
                  {(a.quotes || []).map((q, qi) => (
                    <div key={qi} className="flex items-center gap-2 mb-1">
                      <input
                        className="input-soft flex-1"
                        value={q}
                        onChange={(e) => {
                          const next = [...anime];
                          next[idx].quotes[qi] = e.target.value;
                          setAnime(next);
                        }}
                        data-testid={`studio-anime-${idx}-quote-${qi}`}
                        placeholder="Type a quote you love…"
                      />
                      <button
                        onClick={() => {
                          const next = [...anime];
                          next[idx].quotes.splice(qi, 1);
                          setAnime(next);
                        }}
                        className="p-2"
                        aria-label="Remove"
                      >
                        <X size={12} strokeWidth={1.5} />
                      </button>
                    </div>
                  ))}
                  {(a.quotes || []).length < 3 && (
                    <button
                      onClick={() => {
                        const next = [...anime];
                        next[idx].quotes = [...(next[idx].quotes || []), ""];
                        setAnime(next);
                      }}
                      className="text-xs mt-1 flex items-center gap-1"
                      style={{ color: "var(--primary)" }}
                      data-testid={`studio-anime-${idx}-add-quote`}
                    >
                      <Plus size={12} strokeWidth={1.75} /> Add quote
                    </button>
                  )}
                </div>
              ))}
            </div>
            {anime.length > 0 && (
              <div className="mt-3 flex justify-end">
                <button onClick={saveAnimeQuotes} className="btn-primary text-sm flex items-center gap-2" data-testid="studio-anime-save-btn">
                  <Save size={14} strokeWidth={1.5} /> Save quotes
                </button>
              </div>
            )}
          </StudioBlock>

          {/* Anime Quote Library — browse & add curated quotes */}
          <StudioBlock icon={BookOpen} label="quote library" testid="studio-block-quote-library">
            <QuoteLibraryPanel
              currentUserQuotes={new Set(anime.flatMap((a) => a.quotes || []))}
              onAdded={async () => {
                try {
                  const { data } = await api.get("/me/anime");
                  setAnime((data.items || []).map((a) => ({ ...a, quotes: a.quotes || [] })));
                } catch (e) { /* silent — panel already toasted */ }
              }}
            />
          </StudioBlock>
        </section>
      </div>
    </AppShell>
  );
}

function StudioBlock({ icon: Icon, label, children, testid }) {
  return (
    <div className="card-soft p-5 fade-up" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
        <span className="eyebrow">{label}</span>
      </div>
      {children}
    </div>
  );
}

function Slider({ label, min, max, value, onChange, testid, suffix = "" }) {
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs uppercase tracking-wider text-muted">{label}</span>
        <span className="text-xs" style={{ color: "var(--text)" }}>{value}{suffix}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[var(--primary)]"
        data-testid={testid}
      />
    </div>
  );
}

function Segmented({ value, options, onChange, testid }) {
  return (
    <div className="rounded-full p-1 flex" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }} data-testid={testid}>
      {options.map(([v, label]) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className="flex-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all"
          style={{
            background: value === v ? "var(--primary)" : "transparent",
            color: value === v ? "#fff" : "var(--text)",
          }}
          data-testid={`${testid}-${v}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function MusicTrackPicker({ value, onChange }) {
  const [tracks, setTracks] = React.useState([]);
  React.useEffect(() => {
    api.get("/music/tracks").then(({ data }) => setTracks(data.items || [])).catch(() => {});
  }, []);
  return (
    <div className="mb-3">
      <div className="text-xs uppercase tracking-wider text-muted mb-2">default track</div>
      <div className="flex flex-wrap gap-2" data-testid="studio-music-track-picker">
        {tracks.map((t) => (
          <button
            key={t.id}
            onClick={() => onChange(t.id)}
            className="px-3 py-1.5 rounded-full text-xs font-medium"
            style={{
              background: value === t.id ? "color-mix(in oklab, var(--primary) 22%, var(--surface))" : "var(--surface-2)",
              border: `1px solid ${value === t.id ? "var(--primary)" : "var(--border)"}`,
              color: "var(--text)",
            }}
            data-testid={`studio-music-track-${t.id}`}
            title={`${t.composer} — ${t.duration_seconds}s`}
          >
            {t.composer.split(" ").pop()} · {t.title.split(" ")[0]}
          </button>
        ))}
      </div>
    </div>
  );
}

function PreviewCard({ palette }) {
  return (
    <div className="rounded-3xl overflow-hidden fade-up" style={{
      background: `linear-gradient(135deg, ${palette.background_base}, ${palette.background_gradient_stop} 55%, ${palette.background_gradient_stop})`,
      border: `1px solid ${palette.text_primary}18`,
      boxShadow: "0 24px 44px -22px rgba(50, 30, 40, 0.24)",
    }}>
      <div className="p-6 sm:p-8">
        <div className="text-xs uppercase tracking-wider" style={{ color: palette.accent }}>preview</div>
        <div className="mt-2 rounded-2xl p-5" style={{ background: palette.surface_card, border: `1px solid ${palette.text_primary}18` }}>
          <div className="text-xs uppercase tracking-wider" style={{ color: palette.text_muted }}>your north star</div>
          <div className="mt-1 leading-tight" style={{ fontFamily: "var(--font-display, 'Cormorant Garamond', serif)", color: palette.text_primary, fontSize: "1.5rem", fontStyle: "italic" }}>
            "Psychology & Neuroscience — because how we feel is who we become."
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: palette.primary }} />
              <span className="text-sm" style={{ color: palette.text_primary }}>Maths</span>
            </div>
            <span className="text-xs" style={{ color: palette.text_muted }}>2.25h / 4h</span>
          </div>
          <div className="mt-2 h-1.5 rounded-full" style={{ background: `${palette.primary}25` }}>
            <div className="h-full rounded-full" style={{ width: "56%", background: palette.primary }} />
          </div>
        </div>
        <div className="mt-3 rounded-2xl p-4" style={{ background: `${palette.primary}18`, border: `1px solid ${palette.text_primary}18` }}>
          <div className="text-xs uppercase tracking-wider" style={{ color: palette.accent }}>from your dream</div>
          <blockquote className="italic mt-1 leading-snug" style={{ fontFamily: "var(--font-display, 'Cormorant Garamond', serif)", color: palette.text_primary, fontSize: "1.05rem" }}>
            "Every page of Psychology today writes tomorrow's you."
          </blockquote>
        </div>
      </div>
    </div>
  );
}
